import React from "react";
import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import BadgeIcon from "@mui/icons-material/Badge";
import MessageIcon from "@mui/icons-material/Message";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventNoteIcon from "@mui/icons-material/EventNote";
import PaymentsIcon from "@mui/icons-material/Payments";
import SettingsIcon from "@mui/icons-material/Settings";

// "product" — the day-to-day feature areas. "manage" — running the school
// account itself (staff, settings, support). Mirrors the same MODES/ACCOUNT
// split the consumer app's Learning Hub sidebar uses, just relabeled for
// this app's own two groups.
const ALL_ITEMS = [
  {
    id: "dashboard",
    title: "Dashboard",
    description: "What needs you today",
    path: "/school/dashboard",
    icon: <DashboardIcon />,
    // No module gate. The dashboard is the work queue — who needs you today —
    // and its insights-only panels degrade server-side rather than the whole
    // page being withheld from a school that bought Tutors.
    // Instructors see the product areas the school has paid for. They are the
    // people teaching the cohort the dashboard describes, and hiding it meant
    // the only person who could act on "three students need attention" was the
    // owner, who is not teaching any of them. Gated by MODULE, not by role.
    group: "product",
  },
  {
    id: "students",
    title: "Students",
    description: "Manage students",
    path: "/school/students",
    icon: <PeopleAltIcon />,
    group: "product",
  },
  // Split from one "Tutors" item. It held a CFI's work surface and an owner's
  // P&L behind the same label, so a CFI landed on a page whose other half was
  // the school's revenue, and an owner had to know the month's numbers were
  // behind a tab called "Tutors". Two readers, two items.
  // ONE ITEM, NOT TWO. Group classes were a second item, and two nav items for
  // one person's job is one too many: a class and a 1:1 are both a thing you
  // turn up and teach, and both are already drawn on the same calendar.
  //
  // NO hiddenForRoles, deliberately. operations (Receptionist) does not teach,
  // but calling off Thursday's class is their job, and hiding this is how
  // eight students ended up holding a booking for a class that was not
  // happening. They get the classes list and none of the diary — the page
  // decides what to show them, the nav only has to let them reach it. Role
  // changes what is IN a page; it must never change what a page IS.
  {
    id: "teaching",
    title: "Teaching",
    // Role-neutral on purpose: the receptionist who now reaches this item
    // does not have a week or requests, and a description that says "your"
    // is wrong for half its readers.
    description: "Diary, requests and group classes",
    path: "/school/teaching",
    icon: <EventNoteIcon />,
    requiresModule: "tutors",
    group: "product",
  },
  {
    id: "bookings",
    title: "Bookings & Revenue",
    description: "Booking activity and what the school earns",
    path: "/school/bookings",
    icon: <PaymentsIcon />,
    requiresModule: "tutors",
    // A CFI's earnings are on My Teaching. This is the school's P&L, and
    // showing an instructor their colleagues' revenue is not a permission
    // anybody asked for.
    hiddenForRoles: ["instructor"],
    group: "product",
  },
  {
    id: "staff",
    title: "Staff",
    description: "Manage staff",
    path: "/school/staff",
    icon: <BadgeIcon />,
    hiddenForRoles: ["instructor"],
    group: "manage",
  },
  {
    id: "settings",
    title: "Settings",
    description: "School & Tutors configuration",
    path: "/school/settings",
    icon: <SettingsIcon />,
    onlyForRoles: ["primary_admin", "staff_admin"],
    group: "manage",
  },
  {
    id: "book-meeting",
    title: "Book a Meeting",
    description: "Talk to your account manager",
    path: "/school/book-meeting",
    icon: <EventAvailableIcon />,
    hiddenForRoles: ["instructor"],
    group: "manage",
  },
  {
    id: "feedback",
    title: "Feedback",
    description: "Share your feedback",
    path: "/school/feedback",
    icon: <MessageIcon />,
    group: "manage",
  },
];

// modules: { learn, insights, tutors } for the school currently in view.
// role: the acting admin's own role. An instructor sees Dashboard (scoped to
// their own students), Students, My Teaching and Feedback — nav-only; real
// enforcement is server-side per route.
// Returns { product: [...], manage: [...] } instead of a flat list, so the
// sidebar can render each group under its own section label.
// Items an independent instructor (one-person school) never needs: the
// school's P&L page and the account-manager booking. Everything else is the
// same product, with Staff relabelled "Team" because a group of two is not
// a staff.
const HIDDEN_FOR_INDEPENDENT = ["bookings", "book-meeting"];
const INDEPENDENT_OVERRIDES = {
  staff: { title: "Team", description: "Up to 2 team members" },
  settings: { description: "Your group and payouts" },
};

const schoolSidebarConfig = (modules = {}, role = null, isIndependentInstructor = false) => {
  const visible = ALL_ITEMS.filter((item) => {
    if (isIndependentInstructor && HIDDEN_FOR_INDEPENDENT.includes(item.id)) return false;
    if (item.requiresModule && !modules[item.requiresModule]) return false;
    // Any role, not just instructor. The old form silently ignored every other
    // entry in hiddenForRoles, so a rule written for operations did nothing.
    if (item.hiddenForRoles?.includes(role)) return false;
    if (item.onlyForRoles && !item.onlyForRoles.includes(role)) return false;
    return true;
  }).map((item) => (
    isIndependentInstructor && INDEPENDENT_OVERRIDES[item.id]
      ? { ...item, ...INDEPENDENT_OVERRIDES[item.id] }
      : item
  ));
  return {
    product: visible.filter((item) => item.group === "product"),
    manage: visible.filter((item) => item.group === "manage"),
  };
};

export default schoolSidebarConfig;
